
import React from 'react';
import { UserBadge } from '@/types/pitch';
import { Trophy, Lightbulb, Users } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface BadgesDisplayProps {
  badges: UserBadge[];
  isLoading?: boolean;
}

const BadgesDisplay: React.FC<BadgesDisplayProps> = ({ badges, isLoading }) => {
  if (isLoading) {
    return (
      <div className="flex gap-2">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-8 w-8 rounded-full bg-muted animate-pulse"></div>
        ))}
      </div>
    );
  }

  if (badges.length === 0) {
    return <span className="text-sm text-muted-foreground">No badges yet</span>;
  }

  const badgeIcons = {
    top_thinker: <Trophy className="h-4 w-4 text-yellow-500" />,
    idea_starter: <Lightbulb className="h-4 w-4 text-blue-500" />,
    collab_champ: <Users className="h-4 w-4 text-green-500" />
  };

  const badgeTitles = {
    top_thinker: 'Top Thinker',
    idea_starter: 'Idea Starter',
    collab_champ: 'Collab Champ'
  };

  const badgeDescriptions = {
    top_thinker: 'Earned by receiving 10+ upvotes on your pitches',
    idea_starter: 'Earned by sharing your first pitch idea',
    collab_champ: 'Earned by providing helpful feedback to others'
  };

  return (
    <div className="flex flex-wrap gap-2">
      <TooltipProvider>
        {badges.map((badge) => (
          <Tooltip key={badge.id}>
            <TooltipTrigger asChild>
              <div className="h-8 w-8 rounded-full bg-muted/50 flex items-center justify-center cursor-help border">
                {badgeIcons[badge.badge_type as keyof typeof badgeIcons]}
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <div className="text-sm">
                <p className="font-bold">{badgeTitles[badge.badge_type as keyof typeof badgeTitles]}</p>
                <p className="text-xs text-muted-foreground">
                  {badgeDescriptions[badge.badge_type as keyof typeof badgeDescriptions]}
                </p>
              </div>
            </TooltipContent>
          </Tooltip>
        ))}
      </TooltipProvider>
    </div>
  );
};

export default BadgesDisplay;
